import { createMachine, assign } from 'xstate'
import PropTypes from 'prop-types'
import goals, { GoalType } from 'js/goals'
import genders, { GenderType } from 'js/genders'
import { environment } from 'js/config'

interface AccountFlowContext {
  userData: {
    goal: GoalType | null
    gender: GenderType | null
    height: number | null
    weight: number | null
    goalWeight: number | null
    age: number | null
    heightUnit: string | null
    weightUnit: string | null
    accountMethod: string | null
    firstName: string
    email: string
    password: string
    country: string
    oauth_service: string | null
    oauth_token: string | null
    statsDataValid: boolean
  }
  discount: number | null
  plan: {} | null
  isLoggedIn: boolean
  limitedProfile: {} | null
  onlySignup: boolean
  scenario: string | null
  forcedGoal: boolean
  emailExistsError: boolean
}

export const initialContext: AccountFlowContext = {
  userData: {
    goal: null,
    gender: null,
    height: null,
    weight: null,
    goalWeight: null,
    age: null,
    heightUnit: null,
    weightUnit: null,
    accountMethod: null,
    firstName: '',
    email: '',
    password: '',
    country: '',
    oauth_service: null,
    oauth_token: null,
    statsDataValid: false,
  },
  discount: 0,
  plan: null,
  isLoggedIn: false,
  limitedProfile: null,
  onlySignup: false,
  scenario: null,
  forcedGoal: false,
  emailExistsError: false,
}

const decisionScreen = {
  always: [
    {
      cond: (context) => context.isLoggedIn,
      target: 'subscription',
    },
    {
      cond: (context) => context.scenario === 'renew',
      target: 'logIn',
    },
    {
      cond: (context) => context.limitedProfile,
      target: 'subscription',
    },
    {
      cond: (context) => context.scenario === 'signup',
      target: 'signUp',
    },
    {
      cond: 'onlySignup',
      target: 'signUp',
    },
  ],
  on: {
    PICK_SIGNUP: {
      target: 'signUp',
      actions: ['updateContext'],
    },
    PICK_LOGIN: {
      target: 'logIn',
      actions: ['updateContext'],
    },
  },
}

const signUp = {
  initial: 'start',
  states: {
    start: {
      entry: ['setScenarioToSignUp'],
      always: [
        {
          cond: 'accountMethodStepFulfilled',
          target: 'emailAccountCreation',
        },
        {
          cond: 'statsStepFulfilled',
          target: 'pickAccountMethod',
        },
        {
          cond: 'goalStepFulfilled',
          target: 'stats',
        },
        {
          target: 'goal',
        },
      ],
    },
    goal: {
      on: {
        NEXT: {
          target: 'stats',
          actions: ['updateContext'],
        },
        LOGIN: '#accountFlow.logIn',
      },
    },
    stats: {
      on: {
        PREV: {
          cond: (context) => !context.forcedGoal,
          target: 'goal',
        },
        NEXT: [
          {
            cond: 'skipPickAccountMethod',
            target: 'emailAccountCreation',
          },
          'pickAccountMethod',
        ],
        UPDATE: { actions: ['updateContext'] },
      },
    },
    pickAccountMethod: {
      on: {
        PREV: 'stats',
        NEXT: [
          {
            cond: 'facebookConnected',
            target: 'setUp',
            actions: ['updateContext'],
          },
          {
            target: 'emailAccountCreation',
            actions: ['updateContext'],
          },
        ],
      },
    },
    emailAccountCreation: {
      always: {
        cond: 'facebookConnected',
        target: 'setUp',
      },
      on: {
        PREV: [
          {
            cond: 'skipPickAccountMethod',
            target: 'stats',
          },
          'pickAccountMethod',
        ],
        NEXT: 'setUp',
        UPDATE: { actions: ['updateContext'] },
      },
    },
    setUp: {
      on: {
        EMAIL_EXISTS: {
          target: 'emailAccountCreation',
          actions: ['setEmailExistsError'],
        },
        NEXT: {
          target: '#subscription',
          actions: ['clearUserData', 'clearLimitedProfile', 'setIsLoggedIn'],
        },
      },
    },
  },
}

const subscription = {
  id: 'subscription',
  initial: 'start',
  states: {
    start: {
      always: [
        {
          cond: (context) =>
            logIfNotTest(context) ||
            (context.plan.discount === 100 &&
              (context.isLoggedIn || context.limitedProfile)),
          target: 'freePurchase',
        },
        {
          cond: (context) =>
            context.plan.duration &&
            (context.isLoggedIn || context.limitedProfile),
          target: 'pay',
        },
        {
          cond: (context) => context.discount === 100,
          target: '#accountFlow.decisionScreen',
        },
      ],
    },
    pay: {
      on: {
        CHANGE_PLAN: {
          target: '#accountFlow.choosePlan',
          actions: ['resetPlan'],
        },
        LOGIN: '#accountFlow.logIn',
      },
    },
    freePurchase: {},
  },
}

const logIn = {
  entry: ['setScenarioToUpgrade'],
  on: {
    NEXT: {
      target: 'subscription',
      actions: ['updateContext', 'clearLimitedProfile'],
    },
    PICK_SIGNUP: {
      target: 'signUp',
      actions: ['updateContext'],
    },
  },
}

const accountFlowMachine = createMachine(
  {
    predictableActionArguments: true,
    schema: {
      context: {} as AccountFlowContext,
    },
    id: 'accountFlow',
    initial: 'choosePlan',
    context: initialContext,
    states: {
      decisionScreen,
      signUp,
      logIn,
      choosePlan: {
        always: {
          cond: (context) =>
            logIfNotTest(context) ||
            !!context.plan ||
            context.scenario === 'gympass',
          target: 'decisionScreen',
        },
        on: {
          NEXT: {
            target: 'decisionScreen',
            actions: ['updateContext'],
          },
        },
      },
      subscription,
    },
  },
  {
    actions: {
      setEmailExistsError: assign((context) => ({
        ...context,
        emailExistsError: true,
      })),
      setIsLoggedIn: assign((context) => ({ ...context, isLoggedIn: true })),
      updateContext: assign((context, event) => {
        const { type: _, ...data } = event

        return {
          ...context,
          ...data,
          userData: { ...context.userData, ...data['userData'] },
        }
      }),
      clearUserData: assign((context) => ({
        ...context,
        userData: initialContext.userData,
      })),
      clearLimitedProfile: assign((context) => ({
        ...context,
        limitedProfile: null,
      })),
      resetPlan: assign((context) => {
        return {
          ...context,
          plan: null,
        }
      }),
      setScenarioToSignUp: assign((context) => {
        return {
          ...context,
          scenario:
            context.scenario === 'upgrade' ? 'signup' : context.scenario,
        }
      }),
      setScenarioToUpgrade: assign((context) => {
        return {
          ...context,
          scenario:
            context.scenario === 'signup' ? 'upgrade' : context.scenario,
        }
      }),
    },
    guards: {
      isLoggedIn: (context) => context.isLoggedIn,
      isLimitedProfile: (context) => !!context.limitedProfile,
      goalStepFulfilled: (context) => !!context.userData.goal,
      statsStepFulfilled: (context) => {
        return context.userData.statsDataValid
      },
      accountMethodStepFulfilled: (context) =>
        context.userData.accountMethod === 'email',
      onlySignup: (context) => context.onlySignup,
      facebookConnected: (context) =>
        context.userData.oauth_service === 'facebook',
      skipPickAccountMethod: (context) => context.scenario === 'gympass',
    },
  }
)

export default accountFlowMachine

export const userDataShape = PropTypes.shape({
  gender: PropTypes.oneOf(genders),
  goal: PropTypes.oneOf(goals),
  height: PropTypes.number,
  weight: PropTypes.number,
  goalWeight: PropTypes.number,
  age: PropTypes.number,
  heightUnit: PropTypes.oneOf(['cm', 'ft']),
  weightUnit: PropTypes.oneOf(['kg', 'lbs', 'st']),
})

function logIfNotTest(context) {
  if (environment !== 'test') {
    console.log(context)
  }
  return false
}
